/**
 * Beroepsregistratiecertificaat (Professional Compliance Ledger).
 * - GET /professional/compliance-ledger           — preview van de ledger (auth professional)
 * - GET /professional/export-certificate          — PDF/CSV-download met X-Certificaat-Hash (auth professional)
 * - GET /certificaat-verificatie/:hash            — publieke verificatie (geen login)
 */
import api from './api';
import { foutmelding } from './kwaliteitspaspoortApi';

export { foutmelding };

export type LedgerPijler = 'PE_PUNTEN' | 'INTERVISIE' | 'PRAKTIJKUREN';
export type CertificaatFormaat = 'pdf' | 'csv';

export interface ComplianceRegel {
  ledger_id: string;
  pijler: LedgerPijler;
  status: string;
  /** YYYY-MM-DD */
  datum: string;
  omschrijving: string;
  pe_punten: number | null;
  accreditatie_register: string | null;
  uren: number | null;
  meeting_id: string | null;
  aanwezigheid_pct: number | null;
  stripe_referentie: string | null;
  factuurnummer: string | null;
  regel_hash: string;
}

export interface LedgerTotalen {
  pe_punten: number;
  per_register: Array<{ register: string; punten: number }>;
  intervisie_uren: number;
  intervisie_sessies: number;
  praktijk_uren: number;
  praktijk_facturen: number;
}

export interface ComplianceLedger {
  periode: { van: string; tot: string };
  totalen: LedgerTotalen;
  regels: ComplianceRegel[];
  afgewezen_intervisies: number;
}

export interface CertificaatVerificatie {
  status: 'geldig' | 'ongeldig';
  afwijkingen: string[];
  certificaat: {
    hash: string;
    /** ISO-tijdstip */
    uitgegeven_op: string;
    formaat: CertificaatFormaat;
    periode: { van: string; tot: string };
    professional: {
      naam: string;
      bedrijfsnaam: string | null;
      kvk_nummer: string | null;
      rollen: Array<{ rol: string; register: string | null; nummer: string | null }>;
    };
    totalen: LedgerTotalen;
    regels: ComplianceRegel[];
  };
}

export const haalLedger = async (van: string, tot: string): Promise<ComplianceLedger> =>
  (await api.get('/professional/compliance-ledger', { params: { van, tot } })).data.data;

/** Publiek: geen auth nodig. 404 = onbekend certificaat, 400 = ongeldige hash. */
export const verifieerCertificaat = async (hash: string): Promise<CertificaatVerificatie> =>
  (await api.get(`/certificaat-verificatie/${encodeURIComponent(hash)}`)).data.data;

/** Haal de bestandsnaam uit Content-Disposition (filename*=UTF-8''… of filename="…"). */
function bestandsnaamUitHeader(header: unknown): string | null {
  if (typeof header !== 'string') return null;
  const utf8 = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* val terug op filename= */
    }
  }
  const plat = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plat ? plat[1].trim() : null;
}

function bewaar(blob: Blob, bestandsnaam: string, type: string) {
  const url = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = bestandsnaam;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/**
 * Download het certificaat als PDF of CSV. Geeft de SHA-256 (X-Certificaat-Hash) terug,
 * zodat de UI de verificatielink kan tonen. Fouten komen als Blob binnen → gebruik `foutmelding`.
 */
export async function downloadCertificaat(van: string, tot: string, formaat: CertificaatFormaat): Promise<{ hash: string | null; bestandsnaam: string }> {
  const r = await api.get('/professional/export-certificate', { params: { van, tot, format: formaat }, responseType: 'blob' });
  const headers = r.headers as Record<string, unknown>;
  const bestandsnaam =
    bestandsnaamUitHeader(headers['content-disposition']) ?? `BeLagom-Beroepsregistratiecertificaat-${van}-${tot}.${formaat}`;
  bewaar(r.data as Blob, bestandsnaam, formaat === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8');
  const hash = headers['x-certificaat-hash'];
  return { hash: typeof hash === 'string' && /^[0-9a-f]{64}$/i.test(hash) ? hash.toLowerCase() : null, bestandsnaam };
}

/** Publieke verificatie-URL bij een certificaat-hash. */
export const verificatieUrl = (hash: string) => `${window.location.origin}/verify/certificaat/${hash}`;
